import {
    Chart,
    ChartConfiguration,
    ArcElement,
    PieController,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getHoursAndMinutes } from '../../../../utils/time';
import { RatioEvents } from '@/types/AnalysisResultType';
import { getColors } from '@/services/colorService';

// Chart.jsに必要なコンポーネントを登録
Chart.register(ArcElement, PieController, Title, Tooltip, Legend, ChartDataLabels);

/**
 * ドーナツチャートを作成する
 * @param ctx - Canvasの2Dコンテキスト
 * @param data - データ
 * @returns 作成されたチャートインスタンス
 */
export const createDonutChart = async (
    ctx: CanvasRenderingContext2D | null,
    inputData: RatioEvents,
    options?: {
        formatDuration?: (minutes: number) => string;
    },
): Promise<Chart<'doughnut'> | undefined> => {
    if (!ctx) return undefined;

    // 同一キャンバス上に既存チャートがあれば先に破棄（競合防止）
    try {
        const canvas = ctx.canvas as HTMLCanvasElement;
        const existing = Chart.getChart(canvas);
        if (existing) existing.destroy();
    } catch (_) {
        // noop
    }

    // データ
    const labels = inputData.ratioEventItems.map((item) => item.title);
    const data = inputData.ratioEventItems.map((item) => item.percentage);
    const totalTimes = inputData.ratioEventItems.map((item) => item.totalTime);

    // 全色を一括取得してからローカルでマッピング
    const allColors = await getColors('all');
    const labelToHex = new Map<string, string>();
    allColors.forEach((c) => {
        labelToHex.set(c.label, c.hexColor);
        if (c.originalLabel && !labelToHex.has(c.originalLabel)) {
            labelToHex.set(c.originalLabel, c.hexColor);
        }
    });

    const backgroundColor = labels.map((label) => labelToHex.get(label) || null);

    // 色が見つからない場合はHSL色を生成
    const finalBackgroundColor = backgroundColor.map(
        (hex, idx, arr) => hex || generateHSLColors(arr.length)[idx],
    );

    // チャート設定
    const formatDuration = options?.formatDuration ?? defaultFormatDuration;

    const isMobile =
        typeof window !== 'undefined'
            ? (window.matchMedia && window.matchMedia('(max-width: 640px)').matches) ||
              window.innerWidth <= 640
            : false;

    const config: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor: finalBackgroundColor,
                    borderColor: 'white',
                    borderWidth: 2,
                    datalabels: {
                        formatter: (value, context) =>
                            formatDonutLabel(value, context.dataIndex, labels),
                        color: '#555555',
                        font: { weight: 'bold', size: 16 },
                        anchor: 'center',
                    },
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: !isMobile,
                    position: 'right',
                    align: 'center',
                    labels: {
                        boxWidth: 12,
                        boxHeight: 12,
                        usePointStyle: true,
                        pointStyle: 'rectRounded',
                        padding: 12,
                        font: { size: 11 },
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (context) =>
                            formatTooltipLabel(context.dataIndex, data, totalTimes, formatDuration),
                    },
                },
            },
        },
    };

    try {
        return new Chart(ctx, config);
    } catch (e) {
        // 競合で生成に失敗した場合は既存を破棄して1回だけ再試行
        try {
            const canvas = ctx.canvas as HTMLCanvasElement;
            const existing = Chart.getChart(canvas);
            if (existing) existing.destroy();
            return new Chart(ctx, config);
        } catch {
            return undefined;
        }
    }
};

/**
 * ドーナツチャート用データラベル整形
 */
function formatDonutLabel(value: number, index: number, labels: string[]): string | null {
    if (index === undefined || index >= labels.length) return null;
    if (index < 4 || value >= 10) {
        const label = labels[index];
        return label.length > 8 ? `${label.substring(0, 8)}…` : label;
    }
    return null;
}

/**
 * ツールチップのラベル整形
 */
function formatTooltipLabel(
    index: number,
    data: number[],
    totalTimes: number[],
    formatDuration: (minutes: number) => string,
): string {
    if (index === undefined || index >= data.length || index >= totalTimes.length) return '';
    const durationText = formatDuration(totalTimes[index]);
    return `${data[index]}% (${durationText})`;
}

function defaultFormatDuration(minutes: number): string {
    const [hours, mins] = getHoursAndMinutes(minutes);
    if (hours === 0) return `${mins}分`;
    return `${hours}時間${mins}分`;
}

/**
 * ランダムなHSLカラーリストを生成する
 */
function generateHSLColors(count: number): string[] {
    return Array.from({ length: count }, (_, i) => {
        const hue = (i * 360) / count;
        const saturation = 50;
        const lightness = 74;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    });
}

// 色マッピング関数は削除 - APIベースに移行
